/**
 * What the Home page needs to show about the latest season: the standings and
 * this week's slate (or, once the season is over, its final standings), plus
 * the head-to-head and last-game lookups behind a manager's "this week" card.
 * All of it reads `LeagueHistory` alone.
 */

import type { GameType, LeagueHistory } from "../history";
import type { PersonGame, Record3 } from "../reportBuilder/ledger";
import { buildLedger, countsTowardHistory, recordOf } from "../reportBuilder/ledger";
import { round2 } from "../util/math";
import type { GameRecord } from "./gameRecords";
import { toGameRecord } from "./gameRecords";
import { compareSeasonLines } from "./seasonLines";

export interface StandingRow {
  personId: string;
  teamName: string;
  logoUrl: string | null;
  /** Regular season only. */
  record: Record3;
  pointsFor: number;
  pointsAgainst: number;
  /** Yahoo's current rank while the season is live, the final finish once it's over. */
  rank: number;
}

export interface SlateGame {
  week: number;
  gameType: GameType;
  /** The two managers, in Yahoo's order. */
  personIds: [string, string];
}

export interface LeagueSnapshot {
  season: number;
  /** True once the season is over: the standings are final and there is no slate. */
  isFinished: boolean;
  /** The week being played (the slate's), or null with no slate. */
  week: number | null;
  /** How many teams make the playoffs (Yahoo's setting); null when the season's record doesn't say. */
  playoffTeams: number | null;
  /** Best first. */
  standings: StandingRow[];
  slate: SlateGame[];
}

/** Null when the history has no seasons yet. */
export function buildLeagueSnapshot(history: LeagueHistory): LeagueSnapshot | null {
  const season = history.seasons.at(-1);
  if (!season) return null;
  const ledger = buildLedger(history).filter((g) => g.season === season.season && g.gameType === "regular");

  const seen = new Set<string>();
  const rows = season.teams.flatMap((team) => {
    const personId = history.personByTeamKey[team.teamKey];
    // A person with two teams in one season is a flagged data problem; the first team stands in, as in buildSeasonLines.
    if (!personId || seen.has(personId)) return [];
    seen.add(personId);
    const games = ledger.filter((g) => g.personId === personId);
    return [
      {
        team,
        personId,
        record: recordOf(games),
        pointsFor: round2(games.reduce((sum, g) => sum + g.points, 0)),
        pointsAgainst: round2(games.reduce((sum, g) => sum + g.opponentPoints, 0)),
      },
    ];
  });
  // Yahoo's own ordering when it gave every team a rank (it applies its tiebreakers); otherwise record, then points,
  // with positions numbered from that order. Never a mix, which could repeat or reorder ranks.
  const hasYahooRanks = rows.every((r) => r.team.rank !== null);
  const ranked = hasYahooRanks ? [...rows].sort((a, b) => a.team.rank! - b.team.rank!) : [...rows].sort((a, b) => compareSeasonLines(a, b));

  return {
    season: season.season,
    isFinished: season.isFinished,
    week: season.upcoming[0]?.week ?? null,
    playoffTeams: season.playoffTeams ?? null,
    standings: ranked.map((r, i) => ({
      personId: r.personId,
      teamName: r.team.name,
      logoUrl: r.team.logoUrl,
      record: r.record,
      pointsFor: r.pointsFor,
      pointsAgainst: r.pointsAgainst,
      rank: hasYahooRanks ? r.team.rank! : i + 1,
    })),
    // Consolation games stay: a manager knocked out of the playoffs still has a game this week.
    slate: season.upcoming.flatMap((game) => {
      const [a, b] = game.teamKeys.map((key) => history.personByTeamKey[key]);
      return a && b ? [{ week: game.week, gameType: game.gameType, personIds: [a, b] as [string, string] }] : [];
    }),
  };
}

export interface HeadToHead {
  /** From the first manager's side. */
  record: Record3;
  gamesPlayed: number;
  /** The most recent meeting, from the first manager's side; null if they've never met. */
  lastMeeting: GameRecord | null;
}

/** Playoff games count; consolation games don't, as everywhere else in the history. */
export function headToHead(ledger: readonly PersonGame[], a: string, b: string): HeadToHead {
  const games = ledger.filter((g) => g.personId === a && g.opponentId === b && countsTowardHistory(g));
  const last = games.at(-1);
  return { record: recordOf(games), gamesPlayed: games.length, lastMeeting: last ? toGameRecord(last) : null };
}

/** The manager's most recent finished game, from their side; null before their first. */
export function lastGameOf(ledger: readonly PersonGame[], personId: string): (GameRecord & { result: PersonGame["result"] }) | null {
  const last = ledger.filter((g) => g.personId === personId && countsTowardHistory(g)).at(-1);
  return last ? { ...toGameRecord(last), result: last.result } : null;
}
